/**
 * **拖动平移的手势 + 物理（一根轴）。**
 *
 * 抽牌堆的横排、记牌器的网格、`scroll.ts` 的滚动区都用它 —— 「按下 → 越过阈值
 * 才算拖动 → 松手惯性 / 越界回弹」这套行为**只能有一处出处**，不然三处的阈值、
 * 阻尼、回弹会各走各的，玩家一眼就看得出。
 *
 * 只认**直接操作类指针**（手指/笔）：鼠标走滚轮。拖动一旦成立，会给按下的那个
 * 对象发 `pointercancel`，于是「按下再拖走再松手」不会误触发它；同一个手势随后
 * 生成的 `tap` 用 `justDragged()` 挡掉（`CardActor` 这种裸 `pointertap` 用得上）。
 *
 * ⚠️ 全局的 `globalpointermove` 只在拖动期间挂在**根**（舞台）上：Pixi 的全局移动
 * 事件只发给「当前指针下的对象链」，手指拖出这一块之后就收不到了。舞台铺满画布，
 * 所以在根上听才拿得到整段拖动；松开就摘。
 */

import { Container } from 'pixi.js';
import type { FederatedPointerEvent } from 'pixi.js';
import { drive, undrive } from './motion';
import { uiPad } from './style';

/** 一根轴允许的偏移范围（闭区间） */
export interface PanBounds {
  readonly min: number;
  readonly max: number;
}

export interface PanSpec {
  /** 事件挂在哪 —— 它的孩子按下会冒泡上来 */
  readonly surface: Container;
  readonly axis: 'x' | 'y';
  /** UI 倍率。只用来把拖动阈值换算成设计像素。不给按 1 算 */
  readonly scale?: number;
  /** 允许的偏移范围（运行时读，内容变化后重新算） */
  readonly bounds: () => PanBounds;
  /** 当前偏移 */
  readonly read: () => number;
  /** 写偏移。拖动越界时写的是**越界值**，收尾由这里负责归位 */
  readonly write: (value: number) => void;
  /** 额外的开关（比如抽牌堆塞回异常时不滚）。返回 false 就不起手势 */
  readonly enabled?: () => boolean;
}

export interface Pan {
  /** 指针按下了（还没过阈值也算） */
  readonly armed: boolean;
  /** 正在拖动 */
  readonly dragging: boolean;
  /**
  * **刚刚拖动过吗。**
  *
  * 用来挡掉同一个手势随后生成的 `tap`：Pixi 只按「按下和松开是不是同一个目标」
  * 判 tap，不看中间移动了多远，所以拖动松手后仍会触发一次 tap。
  */
  justDragged(): boolean;
  /** 停掉在跑的惯性/回弹（不动当前偏移） */
  stop(): void;
  destroy(): void;
}

/** 把「按下」判成「拖动」的位移阈值（设计像素，走 uiPad 换算） */
const DRAG_SLOP = 8;
/** 惯性速度的衰减时间常数（ms）：越大甩得越远 */
const MOMENTUM_TAU = 240;
/** 越界回弹的时间常数（ms）：越小弹回越快 */
const SPRING_TAU = 90;
/** 小于这个速度（px/ms）就不再甩，直接停 */
const MIN_MOMENTUM = 0.02;
/** 甩动速度上限（px/ms），免得一划就飞出去 */
const MAX_FLING = 3.2;
/** 越界阻尼系数：手指拖出边界后，实际位移只有手指的这么多 */
const RUBBER = 0.45;
/** 回弹/停下的收尾容差（px） */
const SETTLE = 0.5;
/** 松手后多久内的 tap 算「这次拖动的余波」 */
const TAP_SUPPRESS_MS = 400;

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return value < min ? min : value > max ? max : value;
}

export function attachPan(spec: PanSpec): Pan {
  const { surface, axis } = spec;
  const slop = Math.max(6, uiPad(DRAG_SLOP, spec.scale ?? 1));
  let armed = false;
  let dragging = false;
  let pointerId = -1;
  let start = 0;
  let startOffset = 0;
  let last = 0;
  let lastAt = 0;
  let velocity = 0;
  let pressTarget: Container | null = null;
  let root: Container | null = null;
  let physicsActive = false;
  let dragEndedAt = 0;

  const nowOf = (event: FederatedPointerEvent): number => event.timeStamp || performance.now();
  const posOf = (event: FederatedPointerEvent): number => {
    const local = surface.toLocal(event.global);
    return axis === 'x' ? local.x : local.y;
  };

  const rubber = (raw: number): number => {
    const { min, max } = spec.bounds();
    if (raw > max) return max + (raw - max) * RUBBER;
    if (raw < min) return min + (raw - min) * RUBBER;
    return raw;
  };

  const physics = {
    advance(deltaMS: number): boolean {
      if (surface.destroyed) return false;
      const dt = Math.min(deltaMS, 50);
      const { min, max } = spec.bounds();
      let value = spec.read();
      if (velocity !== 0) {
        value += velocity * dt;
        velocity *= Math.exp(-dt / MOMENTUM_TAU);
        if (Math.abs(velocity) < MIN_MOMENTUM) velocity = 0;
      }
      let bound: number | null = null;
      if (value > max) bound = max;
      else if (value < min) bound = min;
      if (bound !== null) {
        // 惯性撞到边界就交给弹簧：两个一起叠会来回振荡
        velocity = 0;
        value = bound + (value - bound) * Math.exp(-dt / SPRING_TAU);
        if (Math.abs(value - bound) < SETTLE) value = bound;
      }
      spec.write(value);
      const busy = velocity !== 0 || value > max + SETTLE || value < min - SETTLE;
      if (busy) return true;
      spec.write(clamp(value, min, max));
      physicsActive = false;
      return false;
    },
  };

  const ensurePhysics = (): void => {
    if (physicsActive || surface.destroyed) return;
    physicsActive = true;
    drive(physics);
  };

  const stopPhysics = (): void => {
    physicsActive = false;
    undrive(physics);
  };

  const findRoot = (): Container => {
    let node: Container = surface;
    while (node.parent !== null) node = node.parent;
    return node;
  };

  function attachRoot(): void {
    const next = findRoot();
    if (next === root) return;
    root = next;
    root.on('globalpointermove', onGlobalMove);
    root.on('pointerup', onUp);
    root.on('pointerupoutside', onUp);
    root.on('pointercancel', onCancel);
    // node 里跑单测没有 window；浏览器里这是「浏览器夺走触点」的兜底
    if (typeof window !== 'undefined') window.addEventListener('pointercancel', onWindowCancel, true);
  }

  function detachRoot(): void {
    pressTarget = null;
    if (root === null) return;
    root.off('globalpointermove', onGlobalMove);
    root.off('pointerup', onUp);
    root.off('pointerupoutside', onUp);
    root.off('pointercancel', onCancel);
    root = null;
    if (typeof window !== 'undefined') window.removeEventListener('pointercancel', onWindowCancel, true);
  }

  const onGlobalMove = (event: FederatedPointerEvent): void => {
    if (!armed || event.pointerId !== pointerId) return;
    if (surface.destroyed) return;
    const p = posOf(event);
    const t = nowOf(event);
    if (!dragging) {
      if (Math.abs(p - start) < slop) return;
      // 这一刻才承认是拖动：把按住的那个控件那一次按压取消掉
      dragging = true;
      stopPhysics();
      velocity = 0;
      start = p;
      startOffset = spec.read();
      last = p;
      lastAt = t;
      const held = pressTarget;
      if (held !== null && held !== surface && !held.destroyed) {
        held.emit('pointercancel', event);
      }
      return;
    }
    const dt = t - lastAt;
    if (dt > 0 && p !== last) velocity = clamp((p - last) / dt, -MAX_FLING, MAX_FLING);
    last = p;
    lastAt = t;
    spec.write(rubber(startOffset + (p - start)));
  };

  const onUp = (event: FederatedPointerEvent): void => {
    if (!armed || event.pointerId !== pointerId) return;
    const wasDragging = dragging;
    armed = false;
    dragging = false;
    pointerId = -1;
    detachRoot();
    if (!wasDragging) return;
    dragEndedAt = performance.now();
    // 松手前停了一会儿就不算甩：不然「拖到位置、按住、松开」会自己飞走
    if (nowOf(event) - lastAt > 100) velocity = 0;
    const { min, max } = spec.bounds();
    const value = spec.read();
    if (value > max || value < min) {
      velocity = 0;
      ensurePhysics();
      return;
    }
    if (Math.abs(velocity) < MIN_MOMENTUM) {
      velocity = 0;
      spec.write(clamp(value, min, max));
      return;
    }
    ensurePhysics();
  };

  const onCancel = (event: FederatedPointerEvent): void => {
    if (!armed || event.pointerId !== pointerId) return;
    armed = false;
    dragging = false;
    pointerId = -1;
    velocity = 0;
    detachRoot();
    ensurePhysics();
  };

  // Pixi 不转 DOM 的 pointercancel，浏览器夺走触点时只能自己收尾
  function onWindowCancel(): void {
    if (!armed) return;
    armed = false;
    dragging = false;
    pointerId = -1;
    velocity = 0;
    detachRoot();
    ensurePhysics();
  }

  const onDown = (event: FederatedPointerEvent): void => {
    // 鼠标用滚轮；只有直接操作类指针（手指/笔）才拖动
    if (event.button !== 0 || event.pointerType === 'mouse') return;
    if (armed) return;
    if (spec.enabled !== undefined && !spec.enabled()) return;
    const { min, max } = spec.bounds();
    if (max - min <= 0.5) return;
    dragEndedAt = 0;
    armed = true;
    dragging = false;
    pointerId = event.pointerId;
    start = posOf(event);
    last = start;
    startOffset = spec.read();
    lastAt = nowOf(event);
    pressTarget = event.target instanceof Container ? event.target : null;
    attachRoot();
  };

  const cleanup = (): void => {
    stopPhysics();
    armed = false;
    dragging = false;
    pointerId = -1;
    detachRoot();
    surface.off('pointerdown', onDown);
  };

  surface.on('pointerdown', onDown);
  surface.once('destroyed', cleanup);

  return {
    get armed() {
      return armed;
    },
    get dragging() {
      return dragging;
    },
    justDragged() {
      return dragEndedAt !== 0 && performance.now() - dragEndedAt < TAP_SUPPRESS_MS;
    },
    stop() {
      stopPhysics();
      velocity = 0;
    },
    destroy: cleanup,
  };
}
